<template>
  <div class="cl-shell">
    <!-- =========================================================
         Nagłówek: tytuł z akcentem + zestaw akcji kontekstowych.
         Wariant „list" pokazuje delikatny podtytuł i licznik
         elementów; w trybie formularza zmienia się tylko tytuł
         i pojedynczy przycisk „Wróć do listy".
         ========================================================= -->
    <header class="cl-header">
      <!-- Powrót z formularza — przy LEWEJ krawędzi nagłówka, przed tytułem.
           Wszystkie przyciski powrotu w aplikacji stoją po lewej (paski akcji
           kreatorów w bieżącym pomiarze, archiwum i statystykach), więc ten
           trzymał się wcześniej niespójnie prawej strony. Na widoku listy
           nagłówek zostaje czysty — akcje są przy poszczególnych sekcjach niżej. -->
      <button
        v-if="currentView !== 'list'"
        type="button"
        class="button-add-user cl-header__back"
        @click="backToList"
      >
        <span class="ds-btn__arrow" aria-hidden="true">&#8592;</span>
        {{ t('back-to-list') }}
      </button>

      <div class="cl-header__title-wrap">
        <span class="cl-header__accent" aria-hidden="true"></span>
        <div>
          <h1 class="cl-header__title">
            {{ pageTitle }}
            <span
              v-if="currentView === 'list' && (contacts.length + contactGroups.length) > 0"
              class="cl-header__count"
              :aria-label="t('items-count')"
            >{{ contacts.length + contactGroups.length }}</span>
          </h1>
          <p v-if="currentView === 'list'" class="cl-header__subtitle">
            {{ t('contacts-subtitle') }}
          </p>
        </div>
      </div>

    </header>

    <!-- =========================================================
         Statystyki — tylko na widoku listy. 4 tile'y: łącznie kontaktów,
         e-maili, SMS/MMS, grup. Pomaga zorientować się w skali bez
         ręcznego liczenia.
         ========================================================= -->
    <section
      v-if="currentView === 'list' && (contacts.length > 0 || contactGroups.length > 0)"
      class="cl-stats"
      :aria-label="t('statistics')"
    >
      <div class="cl-stat">
        <span class="cl-stat__num">{{ contacts.length }}</span>
        <span class="cl-stat__label">{{ t('contacts-stats-total') }}</span>
      </div>
      <div class="cl-stat cl-stat--email">
        <span class="cl-stat__num">{{ statsByType.emails }}</span>
        <span class="cl-stat__label">{{ t('contacts-stats-emails') }}</span>
      </div>
      <div class="cl-stat cl-stat--sms">
        <span class="cl-stat__num">{{ statsByType.smsMms }}</span>
        <span class="cl-stat__label">{{ t('contacts-stats-sms') }}</span>
      </div>
      <div class="cl-stat cl-stat--groups">
        <span class="cl-stat__num">{{ contactGroups.length }}</span>
        <span class="cl-stat__label">{{ t('contacts-stats-groups') }}</span>
      </div>
    </section>

    <transition name="cl-fade" mode="out-in">
      <!-- ========================== LISTA ========================== -->
      <div v-if="currentView === 'list'" key="list">
        <!-- ================ Sekcja: KONTAKTY ================ -->
        <section class="cl-section" aria-labelledby="cl-sec-contacts">
          <div class="cl-section__head">
            <div class="cl-section__title-wrap">
              <h2 id="cl-sec-contacts" class="cl-section__title">
                {{ t('contacts').toUpperCase() }}
                <span v-if="contacts.length > 0" class="cl-section__badge">{{ contacts.length }}</span>
              </h2>
            </div>
            <div v-if="canManage" class="cl-section__actions">
              <button type="button" class="button-save" @click="openAddContact">
                <span class="cl-btn-icon" aria-hidden="true">+</span>
                {{ t('add') }}
              </button>
              <button type="button" class="button-add-user" @click="openCsv">
                {{ t('import-csv') }}
              </button>
            </div>
          </div>

          <!-- Pasek filtra dla kontaktów — pojawia się przy >5 wpisach. -->
          <div v-if="contacts.length > 5" class="cl-toolbar">
            <label class="cl-search">
              <svg class="cl-search__icon" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="9" cy="9" r="6.5" fill="none" stroke="currentColor" stroke-width="1.7" />
                <line x1="14" y1="14" x2="18" y2="18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
              <input
                v-model="contactFilter"
                type="search"
                class="cl-search__input"
                :placeholder="t('contacts-filter-placeholder')"
                :aria-label="t('search')"
                autocomplete="off"
              />
              <button
                v-if="contactFilter"
                type="button"
                class="cl-search__clear"
                :aria-label="t('cancel')"
                @click="contactFilter = ''"
              >×</button>
            </label>
            <span class="cl-toolbar__count" aria-live="polite">
              {{ filteredContacts.length }} / {{ contacts.length }}
            </span>
          </div>

          <div v-if="contacts.length > 0" class="cl-table-card">
            <table class="cl-table">
              <thead>
                <tr>
                  <th class="cl-col-num">{{ t('ordinal-number') }}</th>
                  <th class="cl-col-type">{{ t('contact-type') }}</th>
                  <th class="cl-col-info">{{ t('contact') }}</th>
                  <th class="cl-col-desc">{{ t('description') }}</th>
                  <th class="cl-col-action">{{ t('action') }}</th>
                </tr>
              </thead>
              <transition-group tag="tbody" name="cl-row">
                <tr
                  v-for="(contact, index) in filteredContacts"
                  :key="contact.id"
                  :class="{ 'cl-row--pending': pendingContactDeleteId === contact.id }"
                >
                  <td class="cl-col-num">{{ index + 1 }}</td>
                  <td class="cl-col-type">
                    <span
                      class="cl-type-pill"
                      :class="typePillClass(contact.type)"
                    >{{ contactTypeLabel(contact.type) }}</span>
                  </td>
                  <td class="cl-col-info">
                    <span class="cl-mono">{{ contact.info }}</span>
                  </td>
                  <td class="cl-col-desc">
                    <span v-if="contact.description">{{ contact.description }}</span>
                    <span v-else class="cl-readonly">—</span>
                  </td>
                  <td class="cl-col-action">
                    <template v-if="canManage">
                      <div v-if="pendingContactDeleteId === contact.id" class="cl-confirm">
                        <button
                          type="button"
                          class="button-delete button-compact"
                          @click="confirmRemoveContact(contact.id)"
                        >{{ t('wanted-confirm-delete') }}</button>
                        <button
                          type="button"
                          class="button-add-user button-compact"
                          @click="cancelDeleteContact"
                        >{{ t('cancel') }}</button>
                      </div>
                      <div v-else class="cl-row-actions">
                        <button
                          type="button"
                          class="button-add-user button-compact"
                          @click="openEditContact(contact)"
                        >{{ t('edit') }}</button>
                        <button
                          type="button"
                          class="button-delete button-compact"
                          @click="requestDeleteContact(contact.id)"
                        >{{ t('delete') }}</button>
                      </div>
                    </template>
                    <span v-else class="cl-readonly">—</span>
                  </td>
                </tr>
              </transition-group>
            </table>

            <div v-if="filteredContacts.length === 0" class="cl-empty-filter">
              <p>{{ t('wanted-filter-no-results') }}</p>
              <button
                type="button"
                class="button-add-user button-compact"
                @click="contactFilter = ''"
              >{{ t('cancel') }}</button>
            </div>
          </div>

          <div v-else class="cl-empty">
            <svg class="cl-empty__icon" viewBox="0 0 64 64" aria-hidden="true">
              <rect x="10" y="16" width="44" height="32" rx="4" fill="#fff" stroke="#cfcfca" stroke-width="2" />
              <path d="M10 22l22 14 22-14" fill="none" stroke="#cfcfca" stroke-width="2" />
            </svg>
            <p class="cl-empty__title">{{ t('no-contacts-on-list') }}</p>
          </div>
        </section>

        <!-- ================ Sekcja: GRUPY KONTAKTÓW ================ -->
        <section class="cl-section" aria-labelledby="cl-sec-groups">
          <div class="cl-section__head">
            <div class="cl-section__title-wrap">
              <h2 id="cl-sec-groups" class="cl-section__title">
                {{ t('contact-groups').toUpperCase() }}
                <span v-if="contactGroups.length > 0" class="cl-section__badge">{{ contactGroups.length }}</span>
              </h2>
            </div>
            <div v-if="canManage" class="cl-section__actions">
              <button
                type="button"
                class="button-save"
                :disabled="contacts.length === 0"
                :title="contacts.length === 0 ? t('contacts-no-contacts-for-group') : ''"
                @click="openAddGroup"
              >
                <span class="cl-btn-icon" aria-hidden="true">+</span>
                {{ t('contacts-create-group') }}
              </button>
            </div>
          </div>

          <div v-if="contactGroups.length > 0" class="cl-table-card">
            <table class="cl-table">
              <thead>
                <tr>
                  <th class="cl-col-num">{{ t('ordinal-number') }}</th>
                  <th class="cl-col-gname">{{ t('contacts-group-name') }}</th>
                  <th class="cl-col-gdesc">{{ t('contacts-group-description') }}</th>
                  <th class="cl-col-gmembers">{{ t('contacts') }}</th>
                  <th class="cl-col-action">{{ t('action') }}</th>
                </tr>
              </thead>
              <transition-group tag="tbody" name="cl-row">
                <tr
                  v-for="(group, index) in contactGroups"
                  :key="group.id"
                  :class="{ 'cl-row--pending': pendingGroupDeleteId === group.id }"
                >
                  <td class="cl-col-num">{{ index + 1 }}</td>
                  <td class="cl-col-gname">
                    <span class="cl-group-name">{{ group.name }}</span>
                  </td>
                  <td class="cl-col-gdesc">
                    <span v-if="group.description">{{ group.description }}</span>
                    <span v-else class="cl-readonly">—</span>
                  </td>
                  <td class="cl-col-gmembers">
                    <div v-if="group.contactIds.length > 0" class="cl-chips">
                      <span
                        v-for="cid in group.contactIds"
                        :key="cid"
                        class="cl-chip"
                      >{{ getContactLabelById(cid) }}</span>
                    </div>
                    <span v-else class="cl-readonly">—</span>
                  </td>
                  <td class="cl-col-action">
                    <template v-if="canManage">
                      <div v-if="pendingGroupDeleteId === group.id" class="cl-confirm">
                        <button
                          type="button"
                          class="button-delete button-compact"
                          @click="confirmRemoveGroup(group.id)"
                        >{{ t('wanted-confirm-delete') }}</button>
                        <button
                          type="button"
                          class="button-add-user button-compact"
                          @click="cancelDeleteGroup"
                        >{{ t('cancel') }}</button>
                      </div>
                      <div v-else class="cl-row-actions">
                        <button
                          type="button"
                          class="button-add-user button-compact"
                          @click="openEditGroup(group)"
                        >{{ t('edit') }}</button>
                        <button
                          type="button"
                          class="button-delete button-compact"
                          @click="requestDeleteGroup(group.id)"
                        >{{ t('delete') }}</button>
                      </div>
                    </template>
                    <span v-else class="cl-readonly">—</span>
                  </td>
                </tr>
              </transition-group>
            </table>
          </div>

          <div v-else class="cl-empty">
            <svg class="cl-empty__icon" viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="22" cy="26" r="8" fill="#fff" stroke="#cfcfca" stroke-width="2" />
              <circle cx="42" cy="26" r="8" fill="#fff" stroke="#cfcfca" stroke-width="2" />
              <path d="M8 50c2-7 8-11 14-11s12 4 14 11" fill="none" stroke="#cfcfca" stroke-width="2" stroke-linecap="round" />
              <path d="M28 50c2-7 8-11 14-11s12 4 14 11" fill="none" stroke="#cfcfca" stroke-width="2" stroke-linecap="round" />
            </svg>
            <p class="cl-empty__title">{{ t('contacts-no-groups') }}</p>
          </div>
        </section>
      </div>

      <!-- ================== WIDOK: dodaj kontakt ================== -->
      <section v-else-if="currentView === 'add-contact'" key="add-contact" class="cl-form">
        <header class="cl-form__head">
          <h3 class="cl-form__title">{{ t('contacts-add-title') }}</h3>
        </header>
        <div class="cl-form__body">
          <div class="cl-form-row">
            <label>{{ t('contacts-source') }} *</label>
            <div class="cl-source-toggle">
              <label class="cl-radio">
                <input type="radio" value="manual" v-model="contactSource" />
                <span>{{ t('contacts-source-manual') }}</span>
              </label>
              <label class="cl-radio">
                <input type="radio" value="user" v-model="contactSource" />
                <span>{{ t('contacts-source-user') }}</span>
              </label>
            </div>
          </div>
          <div class="cl-form-row">
            <label for="contactType">{{ t('contact-type') }} *</label>
            <select id="contactType" v-model.number="contactType" class="cl-form-control">
              <option :value="0">{{ t('email') }}</option>
              <option :value="1">{{ t('contact-type-sms') }}</option>
              <option :value="2">{{ t('contact-type-mms') }}</option>
            </select>
          </div>
          <div v-if="contactSource === 'manual'" class="cl-form-row">
            <label for="contactInfo">{{ t('contact') }} *</label>
            <input
              id="contactInfo"
              v-model="contactInfo"
              type="text"
              class="cl-form-control"
              :placeholder="contactType === 0 ? t('contact-email-example') : t('contact-phone-example')"
              autocomplete="off"
            />
          </div>
          <div v-else class="cl-form-row">
            <label>{{ t('contacts-pick-user') }} *</label>
            <p class="cl-form__hint">{{ t('contacts-pick-user-hint') }}</p>
            <input
              v-model="userSearch"
              type="text"
              class="cl-form-control cl-form-control--search"
              :placeholder="t('contacts-group-members-search-placeholder')"
              autocomplete="off"
            />
            <ul v-if="filteredAssignableUsers.length > 0" class="cl-pick-list">
              <li
                v-for="user in filteredAssignableUsers"
                :key="user.id"
                class="cl-pick-item"
                :class="{ 'cl-pick-item--selected': selectedUserId === user.id }"
                @click="selectedUserId = user.id"
              >
                <label class="cl-pick-item__label" @click.stop>
                  <input type="radio" :value="user.id" v-model="selectedUserId" />
                  <span class="cl-pick-item__info">{{ userLabel(user) }}</span>
                </label>
              </li>
            </ul>
            <p v-else class="cl-pick-empty">
              {{ userSearch ? t('no-matches') : t('contacts-group-no-users') }}
            </p>
          </div>
          <div class="cl-form-row" v-if="contactGroups.length > 0">
            <label>{{ t('contacts-assign-to-groups') }}</label>
            <p class="cl-form__hint">{{ t('contacts-assign-to-groups-hint') }}</p>

            <input
              v-model="contactFormGroupSearch"
              type="text"
              class="cl-form-control cl-form-control--search"
              :placeholder="t('contacts-search-placeholder')"
              autocomplete="off"
            />

            <ul v-if="filteredGroupsForContactForm.length > 0" class="cl-pick-list">
              <li
                v-for="group in filteredGroupsForContactForm"
                :key="group.id"
                class="cl-pick-item"
                :class="{ 'cl-pick-item--selected': contactFormSelectedGroupSet.has(group.id) }"
                @click="toggleContactFormGroup(group.id)"
              >
                <label class="cl-pick-item__label" @click.stop>
                  <input
                    type="checkbox"
                    :checked="contactFormSelectedGroupSet.has(group.id)"
                    @change="toggleContactFormGroup(group.id)"
                  />
                  <span class="cl-pick-item__info">{{ group.name }}</span>
                </label>
              </li>
            </ul>
            <p v-else class="cl-pick-empty">
              {{ contactFormGroupSearch ? t('no-matches') : t('contacts-no-groups') }}
            </p>

            <p v-if="contactFormSelectedGroupIds.length > 0" class="cl-form__counter">
              {{ t('contacts-selected-count', { count: contactFormSelectedGroupIds.length }) }}
            </p>
          </div>
          <div class="cl-form-row">
            <label for="description">{{ t('contact-description') }}</label>
            <input
              id="description"
              v-model="description"
              type="text"
              class="cl-form-control"
              maxlength="100"
            />
          </div>
        </div>
        <footer class="cl-form__foot">
          <span class="cl-form__spacer"></span>
          <button type="button" class="button-add-user" @click="backToList">{{ t('cancel') }}</button>
          <button
            type="button"
            class="button-save"
            :disabled="!canSubmitContact || actionSubmitting"
            @click="submitAddContact"
          >{{ actionSubmitting ? '...' : t('add') }}</button>
        </footer>
      </section>

      <!-- ================== WIDOK: edytuj kontakt ================== -->
      <section v-else-if="currentView === 'edit-contact'" key="edit-contact" class="cl-form">
        <header class="cl-form__head">
          <h3 class="cl-form__title">{{ t('contacts-edit-title') }}</h3>
          <p class="cl-form__hint">{{ t('contacts-edit-hint') }}</p>
        </header>
        <div class="cl-form__body">
          <div class="cl-form-row">
            <label for="editContactType">{{ t('contact-type') }}</label>
            <select id="editContactType" :value="contactType" class="cl-form-control" disabled>
              <option :value="0">{{ t('email') }}</option>
              <option :value="1">{{ t('contact-type-sms') }}</option>
              <option :value="2">{{ t('contact-type-mms') }}</option>
            </select>
          </div>
          <div class="cl-form-row">
            <label for="editContactInfo">{{ t('contact') }} *</label>
            <input
              id="editContactInfo"
              v-model="contactInfo"
              type="text"
              class="cl-form-control"
              :placeholder="contactType === 0 ? t('contact-email-example') : t('contact-phone-example')"
              autocomplete="off"
            />
          </div>
          <div class="cl-form-row">
            <label for="editDescription">{{ t('contact-description') }}</label>
            <input
              id="editDescription"
              v-model="description"
              type="text"
              class="cl-form-control"
              maxlength="100"
            />
          </div>
        </div>
        <footer class="cl-form__foot">
          <span class="cl-form__spacer"></span>
          <button type="button" class="button-add-user" @click="backToList">{{ t('cancel') }}</button>
          <button
            type="button"
            class="button-save"
            :disabled="!contactInfo || actionSubmitting"
            @click="submitEditContact"
          >{{ actionSubmitting ? '...' : t('contacts-save') }}</button>
        </footer>
      </section>

      <!-- ================== WIDOK: import CSV ================== -->
      <section v-else-if="currentView === 'csv'" key="csv" class="cl-form">
        <header class="cl-form__head">
          <h3 class="cl-form__title">{{ t('contacts-csv-title') }}</h3>
          <p class="cl-form__hint">{{ t('contacts-csv-hint') }}</p>
        </header>
        <div class="cl-form__body">
          <div class="cl-form-row">
            <label for="csvFile">{{ t('contacts-csv-file') }}</label>
            <div class="cl-file-input">
              <input
                id="csvFile"
                ref="fileInput"
                type="file"
                accept=".csv"
                @change="handleFileUpload"
              />
              <span v-if="uploadedFileName" class="cl-file-name">
                {{ uploadedFileName }}
                <button type="button" class="cl-file-remove" @click="removeFile" :aria-label="t('cancel')">×</button>
              </span>
            </div>
          </div>

          <div v-if="csvContacts.length > 0">
            <h4 class="cl-form__subtitle">
              {{ t('contacts-from-csv-ready-to-add') }}
              <span class="cl-section__badge">{{ csvContacts.length }}</span>
            </h4>
            <div class="cl-table-card cl-table-card--inline">
              <table class="cl-table">
                <thead>
                  <tr>
                    <th class="cl-col-num">{{ t('ordinal-number') }}</th>
                    <th class="cl-col-type">{{ t('contact-type') }}</th>
                    <th class="cl-col-info">{{ t('contact') }}</th>
                    <th class="cl-col-desc">{{ t('description') }}</th>
                    <th class="cl-col-action">{{ t('action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(contact, index) in csvContacts" :key="contact.id">
                    <td class="cl-col-num">{{ index + 1 }}</td>
                    <td class="cl-col-type">
                      <span class="cl-type-pill" :class="typePillClass(contact.type)">
                        {{ contactTypeLabel(contact.type) }}
                      </span>
                    </td>
                    <td class="cl-col-info"><span class="cl-mono">{{ contact.info }}</span></td>
                    <td class="cl-col-desc">
                      <span v-if="contact.description">{{ contact.description }}</span>
                      <span v-else class="cl-readonly">—</span>
                    </td>
                    <td class="cl-col-action">
                      <button
                        type="button"
                        class="button-delete button-compact"
                        @click="removeCSVContact(index)"
                      >{{ t('delete') }}</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <footer class="cl-form__foot">
          <span class="cl-form__spacer"></span>
          <button type="button" class="button-add-user" @click="backToList">{{ t('cancel') }}</button>
          <button
            type="button"
            class="button-save"
            :disabled="csvContacts.length === 0 || actionSubmitting"
            @click="submitCsvContacts"
          >{{ actionSubmitting ? '...' : t('add-contacts-from-csv') }}</button>
        </footer>
      </section>

      <!-- ================== WIDOK: dodaj/edytuj grupę ================== -->
      <section
        v-else
        :key="currentView"
        class="cl-form"
      >
        <header class="cl-form__head">
          <h3 class="cl-form__title">
            {{ currentView === 'edit-group' ? t('contacts-edit-group-title') : t('contacts-create-group-title') }}
          </h3>
        </header>
        <div class="cl-form__body">
          <div class="cl-form-row">
            <label for="groupName">{{ t('contacts-group-name') }} *</label>
            <input
              id="groupName"
              v-model="groupFormName"
              type="text"
              class="cl-form-control"
              maxlength="128"
            />
          </div>
          <div class="cl-form-row">
            <label for="groupDescription">{{ t('contacts-group-description') }}</label>
            <input
              id="groupDescription"
              v-model="groupFormDescription"
              type="text"
              class="cl-form-control"
              maxlength="255"
            />
          </div>
          <div class="cl-form-row">
            <label>{{ t('contacts') }}</label>
            <p class="cl-form__hint">{{ t('contacts-group-pick-hint') }}</p>

            <input
              v-model="groupFormSearch"
              type="text"
              class="cl-form-control cl-form-control--search"
              :placeholder="t('contacts-search-placeholder')"
              autocomplete="off"
            />

            <ul v-if="filteredContactsForGroupForm.length > 0" class="cl-pick-list">
              <li
                v-for="contact in filteredContactsForGroupForm"
                :key="contact.id"
                class="cl-pick-item"
                :class="{ 'cl-pick-item--selected': groupFormSelectedSet.has(contact.id) }"
                @click="toggleGroupFormContact(contact.id)"
              >
                <label class="cl-pick-item__label" @click.stop>
                  <input
                    type="checkbox"
                    :checked="groupFormSelectedSet.has(contact.id)"
                    @change="toggleGroupFormContact(contact.id)"
                  />
                  <span class="cl-pick-item__type" :class="typePillClass(contact.type)">
                    {{ contactTypeLabel(contact.type) }}
                  </span>
                  <span class="cl-pick-item__info">{{ contact.info }}</span>
                </label>
                <span v-if="contact.description" class="cl-pick-item__desc">{{ contact.description }}</span>
              </li>
            </ul>
            <p v-else class="cl-pick-empty">
              {{ groupFormSearch ? t('no-matches') : t('contacts-no-contacts-for-group') }}
            </p>

            <p v-if="groupFormSelectedIds.length > 0" class="cl-form__counter">
              {{ t('contacts-selected-count', { count: groupFormSelectedIds.length }) }}
            </p>
          </div>
        </div>
        <footer class="cl-form__foot">
          <span class="cl-form__spacer"></span>
          <button type="button" class="button-add-user" @click="backToList">{{ t('cancel') }}</button>
          <button
            type="button"
            class="button-save"
            :disabled="!groupFormName || actionSubmitting"
            @click="submitGroupForm"
          >{{ actionSubmitting ? '...' : (currentView === 'edit-group' ? t('contacts-save') : t('contacts-create-group')) }}</button>
        </footer>
      </section>
    </transition>

    <!-- Pływający toast statusu. -->
  </div>
</template>

<script>
import ContactsListService from '../services/ContactsListService';
import ContactGroupsService from '@/services/ContactGroupsService';
import Papa from 'papaparse';
import { useUserStore } from '@/stores/user.store';
import { useNotificationStore } from '@/stores/notification.store';

export default {
  name: 'ContactList',
  data() {
    return {
      // ============ Stan listy/danych ============
      contacts: [],
      contactGroups: [],

      // ============ Sterowanie widokami ============
      // 'list' | 'add-contact' | 'edit-contact' | 'csv' | 'add-group' | 'edit-group'
      currentView: 'list',
      actionSubmitting: false,

      // ============ Filtr i potwierdzenia usuwania ============
      contactFilter: '',
      pendingContactDeleteId: null,
      pendingGroupDeleteId: null,

      // ============ Formularz: dodaj kontakt ============
      // Źródło kontaktu: 'manual' = ręczny e-mail/telefon (odbiorca zewnętrzny),
      // 'user' = istniejący użytkownik systemu (kontakt tworzony z jego danych → widoczność + powiadomienia).
      contactSource: 'manual',
      contactType: 0,
      contactInfo: '',
      selectedUserId: null,
      userSearch: '',
      description: '',
      // Grupy, do których od razu przypiąć nowy kontakt (symetria do wyboru kontaktów przy grupie).
      contactFormSelectedGroupIds: [],
      contactFormGroupSearch: '',
      // Lista userów systemu do wyboru jako kontakt (współdzielona z pickerem).
      assignableUsers: [],
      assignableUsersLoaded: false,

      // Edytowany kontakt (widok 'edit-contact'); pola formularza współdzielone z dodawaniem.
      editingContactId: null,

      // ============ Formularz: CSV ============
      csvContacts: [],
      uploadedFileName: '',

      // ============ Formularz: grupa (add + edit) ============
      editingGroupId: null,
      groupFormName: '',
      groupFormDescription: '',
      groupFormSearch: '',
      groupFormSelectedIds: [],

      // ============ Status / toast ============
    };
  },
  computed: {
    canManage() {
      const userStore = useUserStore();
      return userStore.canManageContactsGlobally();
    },
    pageTitle() {
      if (this.currentView === 'add-contact') return this.t('contacts-add-title');
      if (this.currentView === 'edit-contact') return this.t('contacts-edit-title');
      if (this.currentView === 'csv')         return this.t('contacts-csv-title');
      if (this.currentView === 'add-group')   return this.t('contacts-create-group-title');
      if (this.currentView === 'edit-group')  return this.t('contacts-edit-group-title');
      return this.t('contacts').toUpperCase();
    },
    statsByType() {
      let emails = 0;
      let smsMms = 0;
      for (const c of this.contacts) {
        const n = Number(c.type);
        if (n === 0) emails++;
        else if (n === 1 || n === 2) smsMms++;
      }
      return { emails, smsMms };
    },
    filteredContacts() {
      const q = this.contactFilter.trim().toLowerCase();
      if (!q) return this.contacts;
      return this.contacts.filter(c => {
        const info = (c.info || '').toLowerCase();
        const desc = (c.description || '').toLowerCase();
        return info.includes(q) || desc.includes(q);
      });
    },
    filteredContactsForGroupForm() {
      const phrase = this.groupFormSearch.trim().toLowerCase();
      if (!phrase) return this.contacts;
      return this.contacts.filter(contact => (
        String(contact.info ?? '').toLowerCase().includes(phrase)
        || String(contact.description ?? '').toLowerCase().includes(phrase)
      ));
    },
    groupFormSelectedSet() {
      return new Set(this.groupFormSelectedIds);
    },
    filteredAssignableUsers() {
      const phrase = this.userSearch.trim().toLowerCase();
      if (!phrase) return this.assignableUsers;
      return this.assignableUsers.filter(user => (
        String(user.username ?? '').toLowerCase().includes(phrase)
        || String(user.firstName ?? '').toLowerCase().includes(phrase)
        || String(user.lastName ?? '').toLowerCase().includes(phrase)
      ));
    },
    canSubmitContact() {
      if (this.contactSource === 'user') {
        return this.selectedUserId !== null && this.selectedUserId !== undefined;
      }
      return !!this.contactInfo;
    },
    filteredGroupsForContactForm() {
      const phrase = this.contactFormGroupSearch.trim().toLowerCase();
      if (!phrase) return this.contactGroups;
      return this.contactGroups.filter(group => (
        String(group.name ?? '').toLowerCase().includes(phrase)
        || String(group.description ?? '').toLowerCase().includes(phrase)
      ));
    },
    contactFormSelectedGroupSet() {
      return new Set(this.contactFormSelectedGroupIds);
    },
  },
  methods: {
    // ---------- Pomocnicze ----------
    normalizeContact(value)     { return String(value ?? '').trim(); },
    normalizeDescription(value) { return String(value ?? '').trim(); },
    normalizeContactType(value) {
      if (value === 0 || value === '0' || String(value).toLowerCase() === 'email')               return 0;
      if (value === 1 || value === '1' || ['phone', 'sms'].includes(String(value).toLowerCase())) return 1;
      if (value === 2 || value === '2' || String(value).toLowerCase() === 'mms')                  return 2;
      return null;
    },
    contactTypeLabel(type) {
      const n = Number(type);
      if (n === 0) return this.t('email');
      if (n === 1) return this.t('contact-type-sms');
      if (n === 2) return this.t('contact-type-mms');
      return '';
    },
    typePillClass(type) {
      const n = Number(type);
      if (n === 0) return 'cl-type-pill--email';
      if (n === 1) return 'cl-type-pill--sms';
      if (n === 2) return 'cl-type-pill--mms';
      return '';
    },
    isValidContact(type, value) {
      if (type === 0) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (type === 1 || type === 2) return /^\+48\d{9}$/.test(value);
      return false;
    },
    parseApiError(error, fallback) {
      if (typeof error === 'string' && error.trim() !== '') return error;
      if (error?.message) return error.message;
      return fallback;
    },
    isNotFoundError(error) {
      return error === 404 || error === '404' || error?.status === 404 || error?.response?.status === 404;
    },
    mapGroupFromApi(group) {
      return {
        id: group.id,
        name: group.name ?? '',
        description: group.description ?? '',
        contactIds: Array.isArray(group.contactIds) ? group.contactIds : [],
        ownerId: group.ownerId ?? null,
      };
    },
    userLabel(user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      return name ? `${user.username} (${name})` : String(user.username ?? `#${user.id}`);
    },
    async ensureAssignableUsersLoaded() {
      if (this.assignableUsersLoaded) return;
      try {
        this.assignableUsers = await ContactGroupsService.getAssignableUsers();
      } catch (error) {
        console.error('Error loading assignable users:', error);
        this.assignableUsers = [];
      } finally {
        this.assignableUsersLoaded = true;
      }
    },
    getContactLabelById(contactId) {
      const contact = this.contacts.find(item => item.id === contactId);
      if (!contact) return `#${contactId}`;
      return `${this.contactTypeLabel(contact.type)}: ${contact.info}`;
    },

    // ---------- Status / toast ----------
    // Wynik operacji pokazuje globalny komponent powiadomień (AppNotifications).
    showStatus(message, type = 'error') {
      useNotificationStore().push(message, type);
    },

    // ---------- Nawigacja widoków ----------
    openAddContact() {
      this.contactSource = 'manual';
      this.contactType = 0;
      this.contactInfo = '';
      this.selectedUserId = null;
      this.userSearch = '';
      this.description = '';
      this.contactFormSelectedGroupIds = [];
      this.contactFormGroupSearch = '';
      this.currentView = 'add-contact';
      this.ensureAssignableUsersLoaded();
    },
    openEditContact(contact) {
      this.editingContactId = contact.id;
      this.contactType = this.normalizeContactType(contact.type) ?? 0;
      this.contactInfo = contact.info ?? '';
      this.description = contact.description ?? '';
      this.currentView = 'edit-contact';
    },
    openCsv() {
      this.csvContacts = [];
      this.uploadedFileName = '';
      this.currentView = 'csv';
    },
    openAddGroup() {
      this.editingGroupId = null;
      this.groupFormName = '';
      this.groupFormDescription = '';
      this.groupFormSearch = '';
      this.groupFormSelectedIds = [];
      this.currentView = 'add-group';
    },
    openEditGroup(group) {
      this.editingGroupId = group.id;
      this.groupFormName = group.name ?? '';
      this.groupFormDescription = group.description ?? '';
      this.groupFormSearch = '';
      this.groupFormSelectedIds = [...(group.contactIds ?? [])];
      this.currentView = 'edit-group';
    },
    backToList() {
      this.currentView = 'list';
      this.pendingContactDeleteId = null;
      this.pendingGroupDeleteId = null;
    },

    // ---------- Usuwanie kontaktu ----------
    requestDeleteContact(id) { this.pendingContactDeleteId = id; },
    cancelDeleteContact()    { this.pendingContactDeleteId = null; },
    async confirmRemoveContact(id) {
      this.pendingContactDeleteId = null;
      try {
        await ContactsListService.delete(id);
        this.contacts = this.contacts.filter(c => c.id !== id);
        this.groupFormSelectedIds = this.groupFormSelectedIds.filter(cid => cid !== id);
        await this.fetchContactGroups();
        this.showStatus(this.t('contacts-contact-deleted'), 'success');
      } catch (error) {
        console.error('Error deleting contact:', error);
        this.showStatus(this.t('contacts-contact-delete-error'), 'error');
      }
    },

    // ---------- Usuwanie grupy ----------
    requestDeleteGroup(id) { this.pendingGroupDeleteId = id; },
    cancelDeleteGroup()    { this.pendingGroupDeleteId = null; },
    async confirmRemoveGroup(id) {
      this.pendingGroupDeleteId = null;
      try {
        await ContactGroupsService.delete(id);
        this.contactGroups = this.contactGroups.filter(g => g.id !== id);
        this.showStatus(this.t('contacts-group-deleted'), 'success');
      } catch (error) {
        console.error('Error deleting contact group:', error);
        this.showStatus(this.parseApiError(error, this.t('contacts-group-delete-error')), 'error');
      }
    },

    // ---------- Dodawanie kontaktu ----------
    async submitAddContact() {
      const normalizedType = this.normalizeContactType(this.contactType);
      const normalizedDescription = this.normalizeDescription(this.description);
      const contactGroupIds = [...new Set(this.contactFormSelectedGroupIds.map(id => Number(id)).filter(Number.isInteger))];

      if (normalizedType === null) {
        this.showStatus(this.t('contacts-invalid-type'), 'error');
        return;
      }

      // Dodanie istniejącego użytkownika jako kontaktu — wartość kontaktu wylicza serwer
      // z danych konta; kontakt zostaje powiązany z userem (widoczność + powiadomienia).
      if (this.contactSource === 'user') {
        if (this.selectedUserId === null || this.selectedUserId === undefined) {
          this.showStatus(this.t('contacts-pick-user-required'), 'error');
          return;
        }

        this.actionSubmitting = true;
        try {
          const response = await ContactsListService.postNewContactForUser(
            this.selectedUserId, normalizedType, normalizedDescription, contactGroupIds
          );
          this.contacts.unshift({
            type: response.typeOfContact,
            info: response.contact,
            description: response.description ?? '',
            id: response.id,
          });
          await this.fetchContactGroups();
          this.showStatus(this.t('contacts-contact-added'), 'success');
          this.currentView = 'list';
        } catch (error) {
          console.error('Error adding user as contact:', error);
          this.showStatus(this.parseApiError(error, this.t('contacts-contact-add-error')), 'error');
        } finally {
          this.actionSubmitting = false;
        }
        return;
      }

      const normalizedContact = this.normalizeContact(this.contactInfo);
      if (!normalizedContact) {
        this.showStatus(this.t('contacts-contact-required'), 'error');
        return;
      }
      if (!this.isValidContact(normalizedType, normalizedContact)) {
        this.showStatus(
          normalizedType === 0
            ? this.t('contacts-invalid-email')
            : this.t('contacts-invalid-phone'),
          'error'
        );
        return;
      }

      this.actionSubmitting = true;
      try {
        const response = await ContactsListService.postNewContact(
          normalizedContact, normalizedType, normalizedDescription, contactGroupIds
        );
        this.contacts.unshift({
          type: response.typeOfContact,
          info: response.contact,
          description: response.description ?? '',
          id: response.id,
        });
        await this.fetchContactGroups();
        this.showStatus(this.t('contacts-contact-added'), 'success');
        this.currentView = 'list';
      } catch (error) {
        console.error('Error adding contact:', error);
        this.showStatus(this.parseApiError(error, this.t('contacts-contact-add-error')), 'error');
      } finally {
        this.actionSubmitting = false;
      }
    },

    // ---------- Edycja kontaktu ----------
    async submitEditContact() {
      const normalizedContact = this.normalizeContact(this.contactInfo);
      const normalizedDescription = this.normalizeDescription(this.description);
      const type = this.normalizeContactType(this.contactType);

      if (!normalizedContact) {
        this.showStatus(this.t('contacts-contact-required'), 'error');
        return;
      }
      if (!this.isValidContact(type, normalizedContact)) {
        this.showStatus(
          type === 0 ? this.t('contacts-invalid-email') : this.t('contacts-invalid-phone'),
          'error'
        );
        return;
      }

      this.actionSubmitting = true;
      try {
        const response = await ContactsListService.update(
          this.editingContactId, normalizedContact, normalizedDescription
        );
        this.contacts = this.contacts.map(c => (c.id === this.editingContactId
          ? { ...c, info: response.contact, description: response.description ?? '' }
          : c));
        this.showStatus(this.t('contacts-contact-updated'), 'success');
        this.currentView = 'list';
        this.editingContactId = null;
      } catch (error) {
        console.error('Error updating contact:', error);
        this.showStatus(this.parseApiError(error, this.t('contacts-contact-update-error')), 'error');
      } finally {
        this.actionSubmitting = false;
      }
    },

    // ---------- Grupa: zapis (add lub edit) ----------
    async submitGroupForm() {
      const normalizedName = String(this.groupFormName ?? '').trim();
      const normalizedDescription = this.normalizeDescription(this.groupFormDescription);
      const contactIds = [...new Set(this.groupFormSelectedIds.map(id => Number(id)).filter(Number.isInteger))];

      if (!normalizedName) {
        this.showStatus(this.t('contacts-group-name-required'), 'error');
        return;
      }

      this.actionSubmitting = true;
      try {
        if (this.currentView === 'edit-group' && this.editingGroupId) {
          const response = await ContactGroupsService.update(
            this.editingGroupId, normalizedName, normalizedDescription, contactIds
          );
          this.contactGroups = this.contactGroups.map(g =>
            g.id === this.editingGroupId ? this.mapGroupFromApi(response) : g
          );
          this.showStatus(this.t('contacts-group-updated'), 'success');
        } else {
          const response = await ContactGroupsService.add(normalizedName, normalizedDescription, contactIds);
          this.contactGroups.unshift(this.mapGroupFromApi(response));
          this.showStatus(this.t('contacts-group-created'), 'success');
        }
        this.currentView = 'list';
        this.editingGroupId = null;
      } catch (error) {
        console.error('Error saving contact group:', error);
        const fb = this.currentView === 'edit-group'
          ? this.t('contacts-group-update-error')
          : this.t('contacts-group-create-error');
        this.showStatus(this.parseApiError(error, fb), 'error');
      } finally {
        this.actionSubmitting = false;
      }
    },
    toggleGroupFormContact(id) {
      const num = Number(id);
      if (this.groupFormSelectedIds.includes(num)) {
        this.groupFormSelectedIds = this.groupFormSelectedIds.filter(x => x !== num);
      } else {
        this.groupFormSelectedIds = [...this.groupFormSelectedIds, num];
      }
    },
    toggleContactFormGroup(id) {
      const num = Number(id);
      if (this.contactFormSelectedGroupIds.includes(num)) {
        this.contactFormSelectedGroupIds = this.contactFormSelectedGroupIds.filter(x => x !== num);
      } else {
        this.contactFormSelectedGroupIds = [...this.contactFormSelectedGroupIds, num];
      }
    },

    // ---------- CSV ----------
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploadedFileName = file.name;
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        delimiter: ';',
        complete: this.addContactsFromCSV,
        error: (error) => {
          console.error('Error reading CSV file:', error);
          this.showStatus(this.t('contacts-csv-parse-error'), 'error');
        },
      });
    },
    addContactsFromCSV(results) {
      this.csvContacts = results.data
        .map(row => ({
          type: this.normalizeContactType(row[0]),
          info: this.normalizeContact(row[1]),
          description: this.normalizeDescription(row[2]),
          id: Date.now() + Math.random(),
        }))
        .filter(c => c.type !== null && c.info && this.isValidContact(c.type, c.info));
    },
    async submitCsvContacts() {
      this.actionSubmitting = true;
      let success = 0;
      let failed = 0;
      for (const contact of this.csvContacts) {
        try {
          if (contact.type === null || !contact.info || !this.isValidContact(contact.type, contact.info)) {
            failed++;
            continue;
          }
          const response = await ContactsListService.postNewContact(
            contact.info, contact.type, contact.description
          );
          this.contacts.unshift({
            type: response.typeOfContact,
            info: response.contact,
            description: response.description ?? '',
            id: response.id,
          });
          success++;
        } catch (error) {
          console.error('Error adding contact from CSV:', error);
          failed++;
        }
      }
      this.csvContacts = [];
      this.uploadedFileName = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.actionSubmitting = false;
      this.currentView = 'list';
      this.showStatus(
        this.t('contacts-csv-import-result', { success, failed }),
        failed === 0 ? 'success' : 'error'
      );
    },
    removeFile() {
      this.uploadedFileName = '';
      this.csvContacts = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    removeCSVContact(index) {
      this.csvContacts.splice(index, 1);
    },

    // ---------- Pobieranie danych ----------
    async fetchContacts() {
      try {
        const response = await ContactsListService.getAll();
        this.contacts = (Array.isArray(response) ? response : []).map(c => ({
          type: c.typeOfContact,
          info: c.contact,
          description: c.description,
          id: c.id,
        }));
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.contacts = [];
          return;
        }
        console.error('Error fetching contacts:', error);
        this.contacts = [];
      }
    },
    async fetchContactGroups() {
      try {
        const response = await ContactGroupsService.getAll();
        this.contactGroups = (Array.isArray(response) ? response : []).map(this.mapGroupFromApi);
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.contactGroups = [];
          return;
        }
        console.error('Error fetching contact groups:', error);
        this.contactGroups = [];
      }
    },
  },
  async mounted() {
    await this.fetchContacts();
    await this.fetchContactGroups();
  },
};
</script>

<style scoped>
/* =============================================================
   Lokalna paleta — utrzymujemy spójność z czarną/białą listą:
   neutralne szarości jako ramy, akcent w tonacji granatowo-zielonej
   (różny od czerwieni BL, by sekcja "kontakty" miała swoją tożsamość).
   ============================================================= */
.cl-shell {
  --cl-accent: var(--accent-blue);          /* akcent tytułowy (zgodny z BL) */
  --cl-accent-soft: var(--surface-tertiary);       /* zielony — akcja konstruktywna */
  --cl-ink: var(--text-primary);
  --cl-ink-soft: var(--text-secondary);
  --cl-muted: var(--text-muted);
  --cl-line: var(--border-default);
  --cl-line-soft: var(--border-subtle);
  --cl-bg: var(--surface-secondary);
  --cl-bg-hover: var(--surface-hover);

  --cl-email:       var(--accent-blue);
  --cl-email-bg:    var(--accent-blue-soft);
  --cl-email-bdr:   var(--accent-blue-border);
  --cl-sms:         var(--status-ok);
  --cl-sms-bg:      var(--status-ok-soft);
  --cl-sms-bdr:     var(--status-ok-border);
  --cl-mms:         var(--status-warn-strong);
  --cl-mms-bg:      var(--status-warn-soft);
  --cl-mms-bdr:     var(--status-warn-border);

  padding: 1.5rem 1.3rem 2.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
  color: var(--cl-ink);
}

/* =============================================================
   Header sekcji — pionowy akcent po lewej, tytuł, podtytuł, licznik.
   ============================================================= */
.cl-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--cl-line);
  position: relative;
}
.cl-header__title-wrap {
  display: flex;
  align-items: stretch;
  gap: 0.9rem;
  flex: 1;
  min-width: 0;
}
.cl-header__accent {
  width: 5px;
  border-radius: 3px;
  background: var(--cl-accent);
  box-shadow: none;
  flex-shrink: 0;
}
.cl-header__title {
  margin: 0;
  font-size: var(--font-size-section);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.04em;
  line-height: 1.15;
  color: var(--cl-ink);
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.cl-header__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 1.6rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: var(--cl-accent);
  color: var(--text-inverse);
  font-size: 0.85rem;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  box-shadow: var(--shadow-panel);
}
.cl-header__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: var(--cl-muted);
  letter-spacing: 0.01em;
}
.cl-header__back {
  flex-shrink: 0;
  align-self: center;
}
.cl-btn-icon {
  font-weight: var(--font-weight-bold);
  font-size: 1rem;
  line-height: 1;
}

/* =============================================================
   Pasek statystyk — 4 tile'y.
   ============================================================= */
.cl-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.cl-stat {
  position: relative;
  background-color: var(--surface-primary);
  border: 1px solid var(--cl-line);
  border-radius: var(--panel-radius);
  padding: 0.85rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
  box-shadow: var(--shadow-panel);
  transition: box-shadow var(--transition-fast), border-color var(--transition-fast);
}
.cl-stat:hover {
  box-shadow: var(--shadow-panel);
}
.cl-stat::before {
  content: "";
  position: absolute;
  right: -10px;
  top: -10px;
  width: 80px;
  height: 80px;
  /* background: var(--surface-secondary); */
  background-size: 9px 9px;
  pointer-events: none;
}
.cl-stat--email::before  { background-size: 9px 9px; }
.cl-stat--sms::before    { background-size: 9px 9px; }
.cl-stat--groups::before { background-size: 9px 9px; }
.cl-stat__num {
  font-size: 1.7rem;
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  color: var(--cl-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.cl-stat__label {
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cl-muted);
}

/* =============================================================
   Sekcje (Kontakty / Grupy kontaktów).
   ============================================================= */
.cl-section {
  margin-bottom: 1.75rem;
}
.cl-section:last-child {
  margin-bottom: 0;
}
.cl-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}
.cl-section__title-wrap { min-width: 0; }
.cl-section__title {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  color: var(--cl-ink);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.cl-section__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.4rem;
  padding: 0 0.5rem;
  border-radius: 999px;
  background-color: var(--surface-secondary);
  color: var(--cl-ink-soft);
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  border: 1px solid var(--cl-line);
}
.cl-section__actions {
  display: flex;
  gap: 0.55rem;
  flex-shrink: 0;
}

/* =============================================================
   Toolbar z filtrem.
   ============================================================= */
.cl-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.cl-search {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  background-color: var(--surface-primary);
  border: 1px solid var(--cl-line);
  border-radius: var(--control-radius);
  padding: 0 0.6rem 0 2.25rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  height: var(--control-height);
}
.cl-search:focus-within {
  border-color: var(--cl-accent);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
}
.cl-search__icon {
  position: absolute;
  left: 0.7rem;
  top: 50%;
  width: 16px;
  height: 16px;
  transform: translateY(-50%);
  color: var(--cl-muted);
  pointer-events: none;
}
.cl-search__input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: var(--cl-ink);
  height: 100%;
  padding: 0;
}
.cl-search__input::placeholder { color: var(--border-strong); }
.cl-search__input::-webkit-search-decoration,
.cl-search__input::-webkit-search-cancel-button { display: none; }
.cl-search__clear {
  background: transparent;
  border: 0;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--cl-muted);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 22px !important;
  min-height: 22px !important;
  line-height: 1;
}
.cl-search__clear:hover { color: var(--cl-accent); background-color: var(--surface-secondary) !important; }
.cl-toolbar__count {
  font-size: 0.82rem;
  font-weight: var(--font-weight-medium);
  color: var(--cl-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* =============================================================
   Karta z tabelą — soft shadow, sticky thead, polish na hover.
   ============================================================= */
.cl-table-card {
  background-color: var(--surface-primary);
  border: 1px solid var(--cl-line);
  border-radius: var(--panel-radius);
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: var(--shadow-panel);
  margin-bottom: 1rem;
}
.cl-table-card--inline {
  margin-top: 0.75rem;
  margin-bottom: 0;
}
.cl-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}
.cl-table td { word-break: break-word; overflow-wrap: anywhere; }
.cl-table th,
.cl-table td {
  padding: 0.75rem 0.95rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--cl-line-soft);
}
.cl-table th.cl-col-num,
.cl-table td.cl-col-num { text-align: right; }
.cl-table th.cl-col-action,
.cl-table td.cl-col-action { text-align: right; white-space: nowrap; }
.cl-table thead th {
  background-color: var(--surface-secondary);
  font-weight: var(--font-weight-bold);
  color: var(--cl-ink-soft);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid var(--cl-ink);
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}
.cl-table tbody tr {
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.cl-table tbody tr:nth-child(even) {
  background-color: var(--cl-bg);
}
.cl-table tbody tr:hover {
  background-color: var(--cl-bg-hover);
  box-shadow: inset 3px 0 0 var(--cl-accent);
}
.cl-table tbody tr:last-child td { border-bottom: 0; }
.cl-table tbody tr.cl-row--pending {
  background-color: var(--status-error-soft);
  box-shadow: inset 3px 0 0 var(--status-error-strong);
}

/* Procentowe szerokości — tabela kontaktów */
.cl-col-num    { width: 5%;  color: var(--cl-muted); font-variant-numeric: tabular-nums; }
.cl-col-type   { width: 14%; }
.cl-col-info   { width: 30%; }
.cl-col-desc   { width: 33%; color: var(--cl-ink-soft); }

/* Procentowe szerokości — tabela grup */
.cl-col-gname    { width: 18%; }
.cl-col-gdesc    { width: 22%; color: var(--cl-ink-soft); }
.cl-col-gmembers { width: 35%; }

/* Wspólne — kolumna akcji */
.cl-col-action { width: 18%; }

td.cl-col-gmembers { overflow: hidden; }

.cl-readonly { color: var(--border-strong); font-weight: 500; }

.cl-row-actions {
  display: inline-flex;
  gap: 0.3rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.cl-confirm {
  display: inline-flex;
  gap: 0.3rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  max-width: 100%;
}
.cl-confirm > .button-compact { flex: 0 0 auto; }

/* =============================================================
   Pille typu kontaktu + dane (monospace) + chipy w komórkach.
   ============================================================= */
.cl-type-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--cl-line);
  background-color: var(--surface-secondary);
  color: var(--cl-ink-soft);
  white-space: nowrap;
}
.cl-type-pill--email { color: var(--cl-email); background-color: var(--cl-email-bg); border-color: var(--cl-email-bdr); }
.cl-type-pill--sms   { color: var(--cl-sms);   background-color: var(--cl-sms-bg);   border-color: var(--cl-sms-bdr); }
.cl-type-pill--mms   { color: var(--cl-mms);   background-color: var(--cl-mms-bg);   border-color: var(--cl-mms-bdr); }

.cl-mono {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 0.9rem;
  color: var(--cl-ink);
}

.cl-group-name {
  font-weight: var(--font-weight-bold);
  color: var(--cl-ink);
}

.cl-chips {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}
.cl-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background-color: var(--surface-secondary);
  border: 1px solid var(--cl-line);
  color: var(--cl-ink-soft);
  font-size: 0.78rem;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =============================================================
   Empty states.
   ============================================================= */
.cl-empty {
  background-color: var(--surface-primary);
  border: 1px dashed var(--border-default);
  border-radius: var(--panel-radius);
  padding: 2.5rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.cl-empty__icon { width: 64px; height: 64px; opacity: 0.85; }
.cl-empty__title { margin: 0; color: var(--cl-ink-soft); font-size: 0.95rem; }

.cl-empty-filter {
  padding: 1.5rem;
  text-align: center;
  color: var(--cl-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
.cl-empty-filter p { margin: 0; }

/* =============================================================
   Formularze (add-contact / csv / add-group / edit-group).
   ============================================================= */
.cl-form {
  background-color: var(--surface-primary);
  border: 1px solid var(--cl-line);
  border-radius: var(--panel-radius);
  overflow: hidden;
  box-shadow: var(--shadow-panel);
}
.cl-form__head {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--cl-line-soft);
  background-color: var(--surface-secondary);
}
.cl-form__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: var(--font-weight-bold);
  color: var(--cl-ink);
  letter-spacing: 0.01em;
}
.cl-form__hint {
  margin: 0.4rem 0 0;
  font-size: 0.88rem;
  color: var(--cl-muted);
}
.cl-form__subtitle {
  margin: 1rem 0 0.5rem;
  font-size: 0.95rem;
  font-weight: var(--font-weight-bold);
  color: var(--cl-ink);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.cl-form__counter {
  margin: 0.45rem 0 0;
  font-size: 0.82rem;
  font-weight: var(--font-weight-medium);
  color: var(--cl-ink-soft);
}
.cl-form__body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cl-form-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.cl-form-row > label {
  font-size: 0.8rem;
  font-weight: var(--font-weight-bold);
  color: var(--cl-ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.cl-form-control {
  width: 100%;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--cl-line);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  font-size: 0.92rem;
  color: var(--cl-ink);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
}
.cl-form-control:focus {
  border-color: var(--cl-email);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
}
.cl-form-control--search { margin-bottom: 0.25rem; }

select.cl-form-control {
  background-color: var(--surface-primary);
  cursor: pointer;
}

/* Plik CSV — wrapper na input z nazwą pliku. */
.cl-file-input {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.cl-file-input input[type="file"] {
  flex: 1;
  min-width: 200px;
  font-size: 0.9rem;
}
.cl-file-name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
  background-color: var(--surface-secondary);
  border: 1px solid var(--cl-line);
  border-radius: 999px;
  font-size: 0.85rem;
  color: var(--cl-ink-soft);
}
.cl-file-remove {
  background: transparent;
  border: 0;
  color: var(--cl-muted);
  font-size: 1.1rem;
  line-height: 1;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 20px !important;
  min-height: 20px !important;
}
.cl-file-remove:hover { color: var(--status-error-strong); background-color: var(--text-inverse) !important; }

/* Lista kontaktów do wyboru w formularzu grupy. */
.cl-pick-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--cl-line);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  max-height: 22rem;
  overflow-y: auto;
}
.cl-pick-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid var(--cl-line-soft);
  cursor: pointer;
  color: var(--cl-ink);
  transition: background-color 0.12s ease;
}
.cl-pick-item:last-child { border-bottom: 0; }
.cl-pick-item:hover { background-color: var(--surface-secondary); }
.cl-pick-item--selected {
  background-color: var(--cl-email-bg);
  box-shadow: inset 3px 0 0 var(--cl-email);
}
.cl-pick-item--selected:hover { background-color: var(--accent-blue-soft); }
.cl-pick-item__label {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex: 1;
  cursor: pointer;
  min-width: 0;
}
.cl-pick-item__label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  accent-color: var(--cl-email);
  cursor: pointer;
}
.cl-pick-item__type {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: var(--font-weight-bold);
  border: 1px solid var(--cl-line);
  background-color: var(--surface-secondary);
  color: var(--cl-ink-soft);
  white-space: nowrap;
}
.cl-pick-item__type.cl-type-pill--email { color: var(--cl-email); background-color: var(--cl-email-bg); border-color: var(--cl-email-bdr); }
.cl-pick-item__type.cl-type-pill--sms   { color: var(--cl-sms);   background-color: var(--cl-sms-bg);   border-color: var(--cl-sms-bdr); }
.cl-pick-item__type.cl-type-pill--mms   { color: var(--cl-mms);   background-color: var(--cl-mms-bg);   border-color: var(--cl-mms-bdr); }
.cl-pick-item__info {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cl-pick-item__desc {
  font-size: 0.82rem;
  color: var(--cl-muted);
  flex-shrink: 0;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cl-pick-empty {
  margin: 0;
  padding: 1.5rem 1rem;
  text-align: center;
  color: var(--cl-muted);
  background-color: var(--surface-secondary);
  border: 1px dashed var(--border-default);
  border-radius: var(--control-radius);
}

.cl-form__foot {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--cl-line-soft);
  gap: 0.55rem;
  background-color: var(--surface-secondary);
}
.cl-form__spacer { flex: 1; }

/* =============================================================
   Animacje.
   ============================================================= */
.cl-fade-enter-active,
.cl-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.cl-fade-enter-from { opacity: 0; transform: translateY(6px); }
.cl-fade-leave-to   { opacity: 0; transform: translateY(-6px); }

.cl-row-enter-active,
.cl-row-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease, background-color 0.25s ease;
}
.cl-row-enter-from {
  opacity: 0;
  transform: translateX(-12px);
  background-color: var(--status-ok-soft);
}
.cl-row-leave-to {
  opacity: 0;
  transform: translateX(12px);
  background-color: var(--status-error-soft);
}
.cl-row-leave-active { position: relative; }


/* =============================================================
   Buttons — override globalnego button-system.css.
   ============================================================= */
.button-save {
  border-radius: 6px !important;
  padding: 0.45rem 0.9rem !important;
}
.button-delete {
  border-radius: 4px;
  padding: 0.45rem 0.75rem;
}

@media (max-width: 900px) {
  .cl-stats { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .cl-shell { padding: 1rem; }
  .cl-header { flex-direction: column; align-items: stretch; gap: 0.75rem; }
  /* Kolumna na wąskich ekranach — powrót zostaje nad tytułem, ale nie rozciąga
     się na całą szerokość (`align-items: stretch` na kontenerze). */
  .cl-header__back { align-self: flex-start; }
  .cl-stats { grid-template-columns: 1fr; }
  .cl-section__head { flex-direction: column; align-items: stretch; }
  .cl-section__actions { width: 100%; }
  .cl-table { font-size: 0.85rem; }
  .cl-table th, .cl-table td { padding: 0.6rem 0.55rem; }
}
</style>
